import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../components/hotkeys';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { guardedExit, resetUnsavedTracking, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { TatPickerDialog } from './TatPickerDialog';
import { priceText } from './contractLabTestPricing';
import {
  DPN_BASE, DPN_FORM_NAME, DPN_NEGATIVE, DPN_PAGE_ID,
  filterDpnRows, firstDpnProblem, isDpnEdited, toDpnInput, toDpnRow,
  type DpnCandidate, type DpnRow,
} from './defaultPricingNew';
import './contractLabTestPricing.css';

/**
 * Settings ▸ Test Pricing ▸ Default Pricing ▸ **New** — the G5 "New" list
 * (TestPriceSurcharge_ListView_newlist, opened by NewObjectAction on the Default
 * Pricing list; TestPriceViewController).
 *
 * - Lists every Matrix + Test (+ Method + Component) + Priority combination that has
 *   no price yet; Matrix, Test, IsGroup, Method, Component, Priority and PrioritySort
 *   are read-only.
 * - TAT (double-click: the TAT tick-list), Surcharge(%), Price, Remark and PriceCodeMatch are typed in
 *   the grid; a negative Surcharge is reset to 0 with G5's warning.
 * - Bar: Export to · Search on one bar; **Save** · **Cancel** right-aligned above AND
 *   below the grid, as G5 draws them.
 * - **Save** stores the ticked rows in one transaction, each with its G5 price code,
 *   then returns to the Default Pricing list. **Cancel** returns without saving.
 *
 * A bespoke page built from the shared grid, toolbar, title and save flow (Contract
 * Lab Test Pricing precedent), as G5 opens a list view here, not a detail view.
 */
export function DefaultPricingNewPage({ onHome, onClose, onSaved }: {
  onHome: () => void;
  /** Back to the Default Pricing list without saving. */
  onClose: () => void;
  /** Back to the Default Pricing list after a save (the list reloads). */
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();

  const [rows, setRows] = useState<DpnRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [tatRow, setTatRow] = useState<DpnRow | null>(null);

  const dirty = useMemo(() => rows.some(isDpnEdited), [rows]);
  // Rule 15: the grid buffer is state the automatic tracker cannot see.
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: DpnCandidate[] }>(`${DPN_BASE}/new-candidates`);
      setRows((res.data?.data ?? []).map(toDpnRow));
      setSelected(new Set());
      resetUnsavedTracking();
    } catch {
      // An empty grid must not pass for "everything is priced" when the list could not be read.
      notify.error('The tests to price could not be loaded. Check that the server is running, then try again.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patch = (key: string, p: Partial<DpnRow>) =>
    setRows((d) => d.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const commitCell = (row: DpnRow, field: string, value: string) => {
    if (field === 'surcharge') {
      // G5 ObjectSpace_ObjectChanged: a negative Surcharge is put back to 0, with a warning.
      const n = Number(value);
      if (value.trim() !== '' && Number.isFinite(n) && n < 0) {
        patch(row.key, { surcharge: '0' });
        notify.warning(DPN_NEGATIVE);
        return;
      }
    }
    if (field === 'surcharge' || field === 'price' || field === 'remark' || field === 'priceCodeMatch') patch(row.key, { [field]: value } as Partial<DpnRow>);
  };

  // ── Save: the ticked rows, one transaction (G5 PriceSaveAction) ───────────
  const save = () => {
    if (saving) return;
    const picked = rows.filter((r) => selected.has(r.key));
    const problem = firstDpnProblem(picked);
    if (problem) {
      if (picked.length === 0) notify.info(problem);
      else notify.warning(problem);
      return;
    }
    let skipped = 0;
    void runSave({
      formName: DPN_FORM_NAME,
      auditAction: 'create',
      save: async () => {
        setSaving(true);
        try {
          const res = await api.post<{ data?: { saved: number; skipped: number } }>(`${DPN_BASE}/save-new`, { rows: picked.map(toDpnInput) });
          skipped = res.data?.data?.skipped ?? 0;
        } finally {
          setSaving(false);
        }
      },
      onSaved: () => {
        resetUnsavedTracking();
        if (skipped > 0) notify.info(`${skipped} of the ticked ${skipped === 1 ? 'row was' : 'rows were'} already priced and left out.`);
        onSaved();
      },
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(msg ?? 'The prices could not be saved.');
      },
    });
  };

  const cancel = () => { if (dirty) void guardedExit(onClose); else onClose(); };

  // Save is a form-specific button, so its key is registered here (rule 14). No Esc for Cancel: Esc
  // already reverts a grid cell being typed in, and must not also leave the page.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'List', order: 1, run: () => save(), disabled: saving || loading },
  ]);

  // G5 TestPriceSurcharge_ListView_newlist columns and captions, in its order.
  const columns: AppColumn<DpnRow>[] = useMemo(() => [
    { field: 'matrix', header: 'Matrix', width: 120 },
    { field: 'test', header: 'Test', width: 170 },
    { field: 'isGroup', header: 'IsGroup', width: 70, align: 'center',
      text: (r) => (r.isGroup ? 'Yes' : 'No'),
      render: (r) => <input type="checkbox" className="clp-readonly-check" checked={r.isGroup} readOnly tabIndex={-1} aria-label="IsGroup" /> },
    { field: 'method', header: 'Method', width: 170 },
    // G5 caption on this list is "Components" (the property name; the Default Pricing list says Component).
    { field: 'component', header: 'Components', width: 110 },
    { field: 'tat', header: 'TAT', width: 130,
      text: (r) => r.tat.join('; '),
      render: (r) => (
        <span
          className={`clp-param${r.tat.length === 0 && selected.has(r.key) ? ' req-empty-cell' : ''}`}
          title="Double-click to choose the TAT"
          onDoubleClick={(e) => { e.stopPropagation(); setTatRow(r); }}
        >
          {r.tat.join('; ') || ' '}
        </span>
      ) },
    { field: 'priority', header: 'Priority', width: 100 },
    { field: 'surcharge', header: 'Surcharge(%)', width: 100, align: 'right', edit: { type: 'number' } },
    { field: 'price', header: 'Price', width: 100, align: 'right',
      text: (r) => priceText(r.price), edit: { type: 'number', display: priceText } },
    { field: 'remark', header: 'Remark', edit: { type: 'text' } },
    { field: 'prioritySort', header: 'PrioritySort', width: 90, align: 'right' },
    // G5 shows PriceCodeMatch editable here but its Save drops it; G6 stores it (the price's Invoice Code).
    { field: 'priceCodeMatch', header: 'PriceCodeMatch', width: 130, edit: { type: 'text' } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selected]);

  const shown = useMemo(() => filterDpnRows(rows, search), [rows, search]);

  // G5 draws Save · Cancel right-aligned above the grid and again below it; one element, rendered twice.
  const saveBar = (
    <div className="dpn-savebar">
      {selected.size > 0 && <span className="clp-pending">{selected.size} ticked</span>}
      <button
        className="ca-btn ca-btn-solid"
        onClick={save}
        disabled={saving || loading}
        title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}
      >
        {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
      </button>
      <button className="ca-btn" onClick={cancel} disabled={saving} title="Cancel">
        <span className="tb-i">✖</span> Cancel
      </button>
    </div>
  );

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>Test Pricing</span>
          <i className="ti ti-chevron-right" />
          <a onClick={cancel} style={{ cursor: 'pointer' }}>Default Pricing</a>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">New</span>
        </div>

        <FormTitle pageId={DPN_PAGE_ID} loadMs={loadMs} loading={loading} auditFormName={DPN_FORM_NAME} />

        <div className="clp-panel">
          {/* G5 bar on the New list: Export to · Search, on one bar. */}
          <AppToolbar
            exportData={{ filename: 'Default Pricing - New', ...gridExportMatrix(shown, columns) }}
            right={(
              <div className="list-search" data-unsaved-ignore>
                <span className="tb-i">🔍</span>
                <input
                  type="text"
                  placeholder="Search…"
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </div>
            )}
          />

          {/* G5 Save · Cancel (PriceSaveAction / PriceCancelAction): right-aligned, above AND below the grid. */}
          {saveBar}

          <AppDataGrid<DpnRow>
            className="dpn-grid"
            rows={shown}
            columns={columns}
            getRowId={(r) => r.key}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={(row, col, v) => commitCell(row, col.field, v == null ? '' : String(v))}
            defaultPageSize={20}
            exportFilename="Default Pricing - New"
            emptyText="Every test already has a price for every priority"
          />

          {saveBar}
        </div>
      </div>

      {tatRow && (
        <TatPickerDialog
          picked={tatRow.tat}
          onClose={() => setTatRow(null)}
          onApply={(tats) => { patch(tatRow.key, { tat: tats }); setTatRow(null); }}
        />
      )}
    </div>
  );
}
